import type { Flight } from '@/lib/api/types';
import type { ReactNode } from 'react';

export interface FlightColumn {
  key: string;
  label: string;
  render: (f: Flight) => ReactNode;
  align?: 'left' | 'right';
}

const DASH = '—';

export function formatDuration(s: number | null): string {
  if (s == null) return DASH;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
}

/** A telemetry number, or `—` when the endpoint did not send it. Every
 *  `FlightTrackPoint` field is nullable on purpose and no missing value is
 *  ever substituted — see the type's doc comment. */
export function formatNum(v: number | null, digits = 0, suffix = ''): string {
  return v == null ? DASH : `${v.toFixed(digits)}${suffix}`;
}

export function formatDistanceKm(m: number | null): string {
  return m == null ? DASH : `${(m / 1000).toFixed(2)} km`;
}

export function formatMaxAlt(f: Flight): string {
  if (f.max_alt_wgs84_m == null || f.max_alt_asl_m == null || f.max_alt_agl_m == null) return DASH;
  return `${f.max_alt_wgs84_m.toFixed(0)} / ${f.max_alt_asl_m.toFixed(0)} / ${f.max_alt_agl_m.toFixed(0)} m`;
}

/** `2026-08-22T19:12:00` → `22 Aug 19:12`. The API's timestamps are naive
 *  (no zone), so they are read as UTC and displayed as given rather than
 *  shifted into a local zone the payload never asserted. */
export function formatStamp(s: string | null): string {
  if (!s) return DASH;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const month = d.toLocaleString('en', { month: 'short', timeZone: 'UTC' });
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${d.getUTCDate()} ${month} ${hh}:${mm}`;
}

export const STATUS_TONE: Record<Flight['flight_status'], string> = {
  Completed: 'text-(--color-green)',
  Active: 'text-(--color-green)',
  Aborted: 'text-(--color-red)',
  Planned: 'text-(--color-amber)',
};

/**
 * The table's columns — ONE set, rendered identically in demo and live.
 *
 * This is the mock's column set. Live does not drop the columns it cannot
 * fill (distance / max alt / data points / sync status); they render '—'
 * instead, so the table has the same shape whichever data source is behind
 * it and a gap reads as a gap rather than as a different screen.
 */
export const FLIGHT_COLUMNS: FlightColumn[] = [
  {
    key: 'flight',
    label: 'Flight',
    render: (f) => f.mission_name ?? f.flight_id,
  },
  {
    key: 'status',
    label: 'Status',
    render: (f) => f.flight_status,
  },
  {
    key: 'aircraft',
    label: 'Aircraft',
    render: (f) => f.aircraft_status ?? DASH,
  },
  {
    key: 'duration',
    label: 'Duration',
    // NOTE: `duration_approx` marks a duration INFERRED from record
    // timestamps, not a measured flight window — see the mapper NOTE in
    // lib/api/flights.ts. The tilde is the whole reason the flag exists;
    // do not drop it when restyling this cell.
    render: (f) =>
      f.duration_approx && f.duration_s != null
        ? `~${formatDuration(f.duration_s)}`
        : formatDuration(f.duration_s),
  },
  {
    key: 'distance',
    label: 'Distance',
    align: 'right',
    render: (f) => formatDistanceKm(f.total_distance_m),
  },
  {
    key: 'maxAlt',
    label: 'Max Alt (WGS84 / ASL / AGL)',
    render: formatMaxAlt,
  },
  {
    key: 'dataPoints',
    label: 'Data Pts',
    align: 'right',
    render: (f) => (f.data_point_count == null ? DASH : String(f.data_point_count)),
  },
  {
    key: 'connector',
    label: 'Connector',
    render: (f) => f.connector ?? DASH,
  },
  {
    key: 'sync',
    label: 'Sync Status',
    render: (f) => f.sync_status,
  },
];
